'use client';

import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { Typography } from '@capsule/consumer-design-system';

import { useTranslations } from 'next-intl';
import { css } from 'styled-components';

import PharmacyIcon from 'public/old_pharmacy.svg';
import PillBottleIcon from 'public/pill-bottle-2.svg';
import MarketingPageSection from 'src/app/components/Elements/MarketingPageSection';
import { useTriggerPharmacyHubDemoMutation } from 'src/app/hooks/data/pharmacyHub';

import { StatusBlobs } from '../../../Order/Tracking/Elements';
import { ClearRxLayout } from '../../ClearRxLayout';
import { medicationItems } from '../constants';
import { ConfirmContainer, ConfirmIconContainer, ConfirmTextContainer, StyledInlineSvg } from '../styles';

import type { SelectedPharmacy } from '.';

interface ConfirmationSuccessProps {
  selectedPharmacy: SelectedPharmacy;
  phone: string;
  doctor: string;
  patient: string;
}

type PriorAuthStep = {
  step: number;
  text: string;
  time: string;
  nextText: string | ReactElement;
};

export const ConfirmationSuccess = (props: ConfirmationSuccessProps) => {
  const { selectedPharmacy, phone, doctor, patient } = props;
  const t = useTranslations('pharmacyHubJourney.confirmationSuccess');
  const { mutateAsync: triggerDemoSms } = useTriggerPharmacyHubDemoMutation();
  const [priorAuthStep, setPriorAuthStep] = useState<PriorAuthStep>({
    step: 1,
    text: t('checkingInsurance'),
    time: '9:40 AM',
    nextText: <>{t('pendingText')}</>,
  });

  useEffect(() => {
    const setStep2 = setTimeout(() => {
      if (phone)
        triggerDemoSms({
          phone: phone,
          variant: 'prior_auth',
          patient_name: patient,
          doctor_name: doctor,
          pharmacy_name: selectedPharmacy.name,
          pharmacy_address: selectedPharmacy.address,
        });
    }, 9000);

    const sendAuthSms = setTimeout(() => {
      setPriorAuthStep({
        step: 2,
        text: t('priorAuth'),
        time: '9:41 AM',
        nextText: <>{t('pendingText')}</>,
      });
    }, 10000);

    const sendSuccessSMS = setTimeout(() => {
      if (phone)
        triggerDemoSms({
          phone: phone,
          variant: 'prior_auth_success',
          patient_name: patient,
          doctor_name: doctor,
          pharmacy_name: selectedPharmacy.name,
          pharmacy_address: selectedPharmacy.address,
        });
    }, 20000);

    const setStep3 = setTimeout(() => {
      setPriorAuthStep({
        step: 3,
        text: t('priorAuthApproved'),
        time: '11:30 AM',
        nextText: <>{t('pendingText')}</>,
      });
    }, 20000);

    const setStep4 = setTimeout(() => {
      setPriorAuthStep({
        step: 4,
        text: t('prescriptionsSent'),
        time: '11:35 AM',
        nextText: selectedPharmacy.name === 'Capsule' ? <>{t('capsuleNextText')}</> : <>{t('pharmacyNextText')}</>,
      });
    }, 30000);

    return () => {
      clearTimeout(setStep2);
      clearTimeout(sendAuthSms);
      clearTimeout(sendSuccessSMS);
      clearTimeout(setStep3);
      clearTimeout(setStep4);
    };
  }, []);

  const successBody = (
    <>
      <ConfirmContainer>
        <ConfirmIconContainer>
          <StyledInlineSvg src={PharmacyIcon} size="22" />
        </ConfirmIconContainer>
        <ConfirmTextContainer>
          <Typography variant="body">
            {selectedPharmacy.name === 'Capsule' ? t('capsulePharmacyName') : selectedPharmacy.name}
          </Typography>
          {selectedPharmacy.name != 'Capsule' && <Typography variant="body">{selectedPharmacy.address}</Typography>}
          {selectedPharmacy.name != 'Capsule' && <Typography variant="body">{selectedPharmacy.phone}</Typography>}
        </ConfirmTextContainer>
      </ConfirmContainer>
      <ConfirmContainer>
        <ConfirmIconContainer>
          <StyledInlineSvg src={PillBottleIcon} size="24" />
        </ConfirmIconContainer>
        <ConfirmTextContainer>
          <Typography variant="body">
            {medicationItems.reduce((acc, med, idx) => {
              if (idx > 0) acc += ', ';
              acc += `${med.name} ${med.dosage}`;
              return acc;
            }, '')}
          </Typography>
        </ConfirmTextContainer>
      </ConfirmContainer>
      <Typography
        variant="overline"
        css={css`
          color: ${({ theme }) => theme.colors.capsuleGreen80};
          margin-bottom: ${({ theme }) => theme.space.s2};
          margin-top: ${({ theme }) => theme.space.s6};
        `}
      >
        {t('whatsNext')}
      </Typography>
      <Typography
        variant="body"
        css={css`
          margin-bottom: ${({ theme }) => theme.space.s5};
        `}
      >
        {priorAuthStep.nextText}
      </Typography>
    </>
  );

  return (
    <MarketingPageSection>
      <ClearRxLayout>
        <Typography
          variant="heading1"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s7};
          `}
        >
          {t('title', {
            status: priorAuthStep.step === 4 ? t('currentStatus.completed') : t('currentStatus.inProgress'),
            pharmacy: selectedPharmacy.name,
          })}
        </Typography>
        <StatusBlobs currentStep={priorAuthStep.step} maxSteps={4} isShowing={true} />
        <Typography
          variant="heading4"
          css={css`
            margin-top: ${({ theme }) => theme.space.s3};
            margin-bottom: ${({ theme }) => theme.space.s1};
          `}
        >
          {priorAuthStep.text}
        </Typography>
        <Typography
          variant="overline"
          css={css`
            color: ${({ theme }) => theme.colors.capsuleGreen80};
            margin-bottom: ${({ theme }) => theme.space.s6};
          `}
        >
          {priorAuthStep.time}
        </Typography>
        {successBody}
      </ClearRxLayout>
    </MarketingPageSection>
  );
};
